import React, { useState, useRef, useEffect } from 'react';
import { Platform, PLATFORM_CONFIG, isComposioPlatform } from '../constants/platforms';
import { profilesApi } from '../services/profilesApi';
import { useAuth } from '../contexts/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import { 
  Wand2, Globe, Layout, AlignLeft, Check, Save, FolderOpen, Plus, Upload, Download, X, 
  Newspaper, Search, Hash, Loader2, Calendar, Repeat, MessageSquarePlus, Image as ImageIcon, 
  Trash2, Building2, User, Cloud, CloudOff, Lock, AlertTriangle, Users, Link2, Zap,
  Twitter, Linkedin, Facebook, Instagram, Video, Youtube, MessageCircle
} from 'lucide-react';

const InputForm = ({ onSubmit, isLoading, userCredits, userTeams = [], activeTeamId, onTeamChange, onNeedUpgrade }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState({
    name: '',
    type: 'business',
    website: '',
    description: '',
    platforms: [],
    instructions: '',
    logo: undefined,
    newsTopics: [],
    useTrendingNews: false,
    generationDays: 7,
    postsPerDay: 1
  });

  const [topicInput, setTopicInput] = useState('');
  const [savedProfiles, setSavedProfiles] = useState([]);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [syncStatus, setSyncStatus] = useState('synced'); // 'synced', 'syncing', 'error'
  const importInputRef = useRef(null);
  const logoInputRef = useRef(null);

  // Load profiles from API (if logged in) or localStorage
  useEffect(() => {
    const loadProfiles = async () => {
      if (user?.uid) {
        setIsLoadingProfiles(true);
        setSyncStatus('syncing');
        try {
          const apiProfiles = await profilesApi.getProfiles(user.uid);
          const formattedProfiles = apiProfiles.map(p => profilesApi.toFrontendFormat(p));
          setSavedProfiles(formattedProfiles);
          setSyncStatus('synced');
        } catch (error) {
          console.error('Failed to load profiles from API:', error);
          // Fallback to localStorage
          const saved = localStorage.getItem('flowgen_profiles');
          if (saved) {
            try {
              setSavedProfiles(JSON.parse(saved));
            } catch (e) {
              setSavedProfiles([]);
            }
          }
          setSyncStatus('error');
        } finally {
          setIsLoadingProfiles(false);
        }
      } else {
        // Not logged in - use localStorage
        const saved = localStorage.getItem('flowgen_profiles');
        if (saved) {
          try {
            setSavedProfiles(JSON.parse(saved));
          } catch (e) {
            setSavedProfiles([]);
          }
        }
      }
    };
    
    loadProfiles();
  }, [user]);

  const togglePlatform = (p) => {
    setProfile(prev => {
      if (prev.platforms.includes(p)) {
        return { ...prev, platforms: prev.platforms.filter(pl => pl !== p) };
      }
      return { ...prev, platforms: [...prev.platforms, p] };
    });
  };

  const addTopic = () => {
    if (topicInput.trim() && !profile.newsTopics?.includes(topicInput.trim())) {
      setProfile(prev => ({
        ...prev,
        newsTopics: [...(prev.newsTopics || []), topicInput.trim()]
      }));
      setTopicInput('');
    }
  };

  const removeTopic = (topic) => {
    setProfile(prev => ({
      ...prev,
      newsTopics: prev.newsTopics?.filter(t => t !== topic)
    }));
  };

  // Get max profiles allowed for user
  const getMaxProfiles = () => {
    if (!userCredits) return 3; // Default free tier
    if (userCredits.profiles_available === -1) return Infinity; // Unlimited
    return userCredits.profiles_available || 3;
  };

  const canSaveNewProfile = () => {
    // If editing existing profile, always allow
    if (profile.id) return true;
    // Check if under limit
    return savedProfiles.length < getMaxProfiles();
  };

  const handleSaveProfile = async () => {
    if (!profile.name) {
      alert("Please enter a Name to save.");
      return;
    }
    
    // Check profile limit for new profiles
    if (!profile.id && !canSaveNewProfile()) {
      onNeedUpgrade?.('Save More Profiles', `profile slots (you have ${savedProfiles.length}/${getMaxProfiles()})`);
      return;
    }
    
    setIsSaving(true);
    setSyncStatus('syncing');
    
    try {
      if (user?.uid) {
        // Save to cloud (API)
        let savedProfile;
        if (profile.id) {
          // Update existing profile
          savedProfile = await profilesApi.updateProfile(profile.id, {
            ...profile,
            userId: user.uid
          });
        } else {
          // Create new profile
          savedProfile = await profilesApi.createProfile({
            ...profile,
            userId: user.uid
          });
        }
        
        const formattedProfile = profilesApi.toFrontendFormat(savedProfile);
        
        // Update local state
        const updatedList = profile.id 
          ? savedProfiles.map(p => p.id === profile.id ? formattedProfile : p)
          : [...savedProfiles, formattedProfile];
        
        setSavedProfiles(updatedList);
        setProfile(formattedProfile);
        setSyncStatus('synced');
      } else {
        // Save to localStorage (not logged in)
        const newProfile = { ...profile, id: profile.id || uuidv4() };
        const updatedList = profile.id 
          ? savedProfiles.map(p => p.id === profile.id ? newProfile : p)
          : [...savedProfiles, newProfile];

        localStorage.setItem('flowgen_profiles', JSON.stringify(updatedList));
        setSavedProfiles(updatedList);
        setProfile(newProfile);
      }
      
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 2000);
    } catch (e) {
      console.error('Save error:', e);
      setSyncStatus('error');
      alert("Failed to save profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoadProfile = (e) => {
    const id = e.target.value;
    const found = savedProfiles.find(p => p.id === id);
    if (found) {
      setProfile({
        ...found,
        type: found.type || 'business',
        newsTopics: found.newsTopics || [],
        useTrendingNews: found.useTrendingNews || false,
        generationDays: found.generationDays || 7,
        postsPerDay: found.postsPerDay || 1,
        logo: found.logo || undefined
      });
    }
  };

  const handleDeleteProfile = async () => {
    if (!profile.id) {
      alert("No profile selected to delete.");
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete "${profile.name}"? This cannot be undone.`)) {
      return;
    }
    
    setIsSaving(true);
    setSyncStatus('syncing');
    
    try {
      if (user?.uid) {
        // Delete from cloud
        await profilesApi.deleteProfile(profile.id);
        
        // Update local state
        const updatedList = savedProfiles.filter(p => p.id !== profile.id);
        setSavedProfiles(updatedList);
        setSyncStatus('synced');
      } else {
        // Delete from localStorage
        const updatedList = savedProfiles.filter(p => p.id !== profile.id);
        localStorage.setItem('flowgen_profiles', JSON.stringify(updatedList));
        setSavedProfiles(updatedList);
      }
      
      // Reset to new profile
      handleNewProfile();
      alert("Profile deleted successfully!");
    } catch (e) {
      console.error('Delete error:', e);
      setSyncStatus('error');
      alert("Failed to delete profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleNewProfile = () => {
    setProfile({
      id: undefined,
      name: '',
      type: 'business',
      website: '',
      description: '',
      platforms: [],
      instructions: '',
      logo: undefined,
      newsTopics: [],
      useTrendingNews: false,
      generationDays: 7,
      postsPerDay: 1
    });
  };

  const handleExport = () => {
    if (!profile.name) {
      alert("Please enter a name before exporting.");
      return;
    }
    const dataStr = JSON.stringify(profile, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `${profile.name.replace(/\s+/g, '-').toLowerCase()}-profile.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const importedProfile = JSON.parse(event.target?.result);
        if (importedProfile.name) {
          setProfile({
            ...importedProfile,
            type: importedProfile.type || 'business',
            id: undefined 
          });
          alert("Profile imported successfully!");
        }
      } catch (err) {
        alert("Failed to parse the profile file.");
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert("File size too large. Please upload an image under 2MB.");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfile(prev => ({ ...prev, logo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const removeLogo = () => {
    setProfile(prev => ({ ...prev, logo: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (profile.platforms.length === 0) {
      alert("Please select at least one platform.");
      return;
    }
    onSubmit(profile);
  };

  return (
    <div className="max-w-2xl mx-auto bg-brand-gray-900 backdrop-blur-sm border border-brand-gray-700 p-8 rounded-2xl shadow-2xl mb-20" data-testid="input-form">
      <div className="mb-8 text-center relative">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-orange/10 text-brand-orange mb-4 ring-1 ring-brand-orange/30">
          <Wand2 className="w-6 h-6" />
        </div>
        <h2 className="text-2xl font-headline uppercase tracking-wider text-white">Create Your Content Strategy</h2>
      </div>

      {/* Profile Type Selector */}
      <div className="mb-8">
        <label className="text-xs font-bold text-brand-gray-500 uppercase tracking-widest block mb-4 text-center">Select Profile Type</label>
        <div className="grid grid-cols-2 gap-4">
          <button 
            type="button"
            onClick={() => setProfile({...profile, type: 'business'})}
            data-testid="business-type-btn"
            className={`flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all ${profile.type === 'business' ? 'border-brand-orange bg-brand-orange/10 shadow-lg shadow-brand-orange/10' : 'border-brand-gray-700 bg-black/50 hover:border-brand-gray-600'}`}
          >
            <Building2 className={`w-6 h-6 ${profile.type === 'business' ? 'text-brand-orange' : 'text-brand-gray-500'}`} />
            <div className="text-center">
              <p className={`text-sm font-bold ${profile.type === 'business' ? 'text-white' : 'text-brand-gray-400'}`}>Business</p>
              <p className="text-[10px] text-brand-gray-500">Brands & Companies</p>
            </div>
          </button>
          <button 
            type="button"
            onClick={() => setProfile({...profile, type: 'personal'})}
            data-testid="personal-type-btn"
            className={`flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all ${profile.type === 'personal' ? 'border-brand-orange bg-brand-orange/10 shadow-lg shadow-brand-orange/10' : 'border-brand-gray-700 bg-black/50 hover:border-brand-gray-600'}`}
          >
            <User className={`w-6 h-6 ${profile.type === 'personal' ? 'text-brand-orange' : 'text-brand-gray-500'}`} />
            <div className="text-center">
              <p className={`text-sm font-bold ${profile.type === 'personal' ? 'text-white' : 'text-brand-gray-400'}`}>Personal</p>
              <p className="text-[10px] text-brand-gray-500">Individual & Influencer</p>
            </div>
          </button>
        </div>
      </div>

      {/* Profile Management */}
      <div className="mb-6 p-4 bg-black/50 rounded-xl border border-brand-gray-700 flex flex-col gap-4">
        {/* Cloud Sync Status */}
        {user && (
          <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${
            syncStatus === 'synced' ? 'bg-green-900/20 text-green-400 border border-green-800/50' :
            syncStatus === 'syncing' ? 'bg-brand-orange/10 text-brand-orange border border-brand-orange/30' :
            'bg-red-900/20 text-red-400 border border-red-800/50'
          }`}>
            {syncStatus === 'synced' && <><Cloud className="w-4 h-4" /> Profiles synced to cloud - accessible from any device</>}
            {syncStatus === 'syncing' && <><Loader2 className="w-4 h-4 animate-spin" /> Syncing profiles...</>}
            {syncStatus === 'error' && <><CloudOff className="w-4 h-4" /> Sync error - using local storage</>}
          </div>
        )}
        {!user && (
          <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-amber-900/20 text-amber-400 border border-amber-800/50">
            <CloudOff className="w-4 h-4" /> Sign in to sync profiles across devices
          </div>
        )}
        
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-3 w-full md:w-auto flex-grow">
            <FolderOpen className="w-5 h-5 text-brand-orange shrink-0" />
            <select 
              value={profile.id || ''} 
              onChange={handleLoadProfile}
              disabled={isLoadingProfiles}
              data-testid="load-profile-select"
              className="bg-brand-gray-800 text-white border border-brand-gray-600 text-sm rounded-lg focus:ring-brand-orange focus:border-brand-orange block w-full p-2.5 disabled:opacity-50"
            >
              <option value="" disabled>
                {isLoadingProfiles ? 'Loading profiles...' : savedProfiles.length === 0 ? 'No saved profiles' : 'Load a saved account...'}
              </option>
              {savedProfiles.map(p => <option key={p.id} value={p.id}>{p.name} ({p.type || 'business'})</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={handleNewProfile} title="New Profile" className="p-2 text-brand-gray-300 bg-brand-gray-800 border border-brand-gray-600 rounded-lg hover:bg-brand-gray-700"><Plus className="w-5 h-5" /></button>
            <button type="button" onClick={handleImportClick} title="Import Profile" className="p-2 text-brand-gray-300 bg-brand-gray-800 border border-brand-gray-600 rounded-lg hover:bg-brand-gray-700"><Upload className="w-5 h-5" /></button>
            <button type="button" onClick={handleExport} title="Export Profile" className="p-2 text-brand-gray-300 bg-brand-gray-800 border border-brand-gray-600 rounded-lg hover:bg-brand-gray-700"><Download className="w-5 h-5" /></button>
            {profile.id && (
              <button 
                type="button" 
                onClick={handleDeleteProfile} 
                title="Delete Profile" 
                disabled={isSaving}
                className="p-2 text-red-400 bg-brand-gray-800 border border-red-800/50 rounded-lg hover:bg-red-900/30 hover:text-red-300 disabled:opacity-50"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
            <input type="file" ref={importInputRef} onChange={handleImport} accept=".json" className="hidden" />
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-brand-gray-300 flex items-center gap-2"><Layout className="w-4 h-4" /> {profile.type === 'business' ? 'Business' : 'Person'} Name</label>
            <input 
              type="text" 
              required 
              value={profile.name} 
              onChange={e => setProfile({...profile, name: e.target.value})} 
              placeholder={profile.type === 'business' ? "e.g. Acme Co." : "e.g. Jane Doe"} 
              data-testid="profile-name-input"
              className="w-full bg-black border border-brand-gray-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-all outline-none" 
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-brand-gray-300 flex items-center gap-2">
              <Globe className="w-4 h-4" /> 
              {profile.type === 'business' ? 'Website' : 'Portfolio Link (Optional)'}
            </label>
            <input 
              type="url" 
              value={profile.website} 
              onChange={e => setProfile({...profile, website: e.target.value})} 
              placeholder="https://example.com" 
              data-testid="website-input"
              className="w-full bg-black border border-brand-gray-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-all outline-none" 
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-brand-gray-300 flex items-center gap-2"><AlignLeft className="w-4 h-4" /> {profile.type === 'business' ? 'Business' : 'Profile'} Description</label>
          <textarea 
            required 
            value={profile.description} 
            onChange={e => setProfile({...profile, description: e.target.value})} 
            placeholder={profile.type === 'business' ? "What do you do? Who is your audience?" : "Who are you? What topics do you cover?"} 
            rows={3} 
            data-testid="description-input"
            className="w-full bg-black border border-brand-gray-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-all outline-none resize-none" 
          />
        </div>

        {/* Logo Upload */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-brand-gray-300 flex items-center gap-2"><ImageIcon className="w-4 h-4" /> {profile.type === 'business' ? 'Business Logo' : 'Profile Picture'} (Optional)</label>
          <div className="flex items-center gap-4 p-4 bg-black border border-brand-gray-700 rounded-lg">
            {profile.logo ? (
              <div className="flex items-center gap-4 w-full">
                <div className="relative w-12 h-12 bg-brand-gray-800 rounded-md border border-brand-gray-600 flex items-center justify-center overflow-hidden">
                  <img src={profile.logo} alt="Logo" className="w-full h-full object-contain" />
                </div>
                <div className="flex-grow">
                  <p className="text-sm text-green-400 font-medium flex items-center gap-1"><Check className="w-3 h-3" /> Image Uploaded</p>
                  <p className="text-xs text-brand-gray-500">Will be overlaid on generated visuals.</p>
                </div>
                <button type="button" onClick={removeLogo} className="p-2 text-red-400 hover:bg-red-900/20 rounded-lg transition-colors" title="Remove Logo">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div onClick={() => logoInputRef.current?.click()} className="flex items-center gap-3 w-full cursor-pointer group">
                <div className="p-2 bg-brand-gray-800 rounded-md border border-brand-gray-600 text-brand-gray-400 group-hover:text-white transition-colors">
                  <Upload className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-brand-gray-300 group-hover:text-brand-orange transition-colors">Click to upload {profile.type === 'business' ? 'logo' : 'image'}</p>
                  <p className="text-xs text-brand-gray-500">PNG or JPG (Max 2MB)</p>
                </div>
                <input type="file" ref={logoInputRef} onChange={handleLogoUpload} accept="image/png, image/jpeg" className="hidden" />
              </div>
            )}
          </div>
        </div>

        {/* Generation Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-brand-gray-300 flex items-center gap-2"><Calendar className="w-4 h-4" /> Generation Days</label>
            <select 
              value={profile.generationDays} 
              onChange={e => setProfile({...profile, generationDays: parseInt(e.target.value)})} 
              data-testid="generation-days-select"
              className="w-full bg-black border border-brand-gray-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-brand-orange focus:border-brand-orange outline-none"
            >
              <option value={7}>7 Days</option>
              <option value={14}>14 Days</option>
              <option value={30}>30 Days</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-brand-gray-300 flex items-center gap-2"><Repeat className="w-4 h-4" /> Posts Per Day (Per Platform)</label>
            <div className="flex items-center gap-4 bg-black border border-brand-gray-700 rounded-lg px-4 py-2">
              <input 
                type="range" 
                min="1" 
                max="5" 
                value={profile.postsPerDay} 
                onChange={e => setProfile({...profile, postsPerDay: parseInt(e.target.value)})} 
                data-testid="posts-per-day-slider"
                className="flex-grow accent-brand-orange" 
              />
              <span className="text-brand-orange font-bold w-4">{profile.postsPerDay}</span>
            </div>
          </div>
        </div>

        {/* Custom Instructions */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-brand-gray-300 flex items-center gap-2"><MessageSquarePlus className="w-4 h-4" /> Custom Instructions (Optional)</label>
          <textarea 
            value={profile.instructions} 
            onChange={e => setProfile({...profile, instructions: e.target.value})} 
            placeholder="e.g. Focus on our new sale, use a humorous tone...&#10;&#10;💡 Tip: Paste a URL to generate posts based on that content (blog post, article, product page)" 
            rows={3} 
            data-testid="instructions-input"
            className="w-full bg-black border border-brand-gray-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-all outline-none resize-none placeholder:text-brand-gray-500" 
          />
        </div>

        {/* Trending News Toggle */}
        <div className="p-5 bg-brand-orange/5 border border-brand-orange/20 rounded-xl space-y-4">
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm font-bold text-brand-orange">
              <Newspaper className="w-4 h-4" /> Research Trending News
            </label>
            <button 
              type="button"
              onClick={() => setProfile({...profile, useTrendingNews: !profile.useTrendingNews})}
              data-testid="trending-news-toggle"
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${profile.useTrendingNews ? 'bg-brand-orange' : 'bg-brand-gray-700'}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${profile.useTrendingNews ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
          
          {profile.useTrendingNews && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <div className="relative flex-grow">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gray-500" />
                  <input 
                    type="text" 
                    value={topicInput} 
                    onChange={e => setTopicInput(e.target.value)} 
                    onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addTopic())} 
                    placeholder="e.g. AI trends, local events..." 
                    data-testid="topic-input"
                    className="w-full bg-black border border-brand-gray-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-brand-orange" 
                  />
                </div>
                <button type="button" onClick={addTopic} className="px-3 py-2 bg-brand-orange text-black text-xs font-bold rounded-lg hover:bg-brand-orange/90">Add</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {profile.newsTopics?.map(topic => (
                  <span key={topic} className="flex items-center gap-1.5 px-2 py-1 bg-brand-gray-800 text-brand-gray-300 border border-brand-gray-700 rounded-md text-xs font-medium">
                    <Hash className="w-3 h-3 text-brand-orange" /> {topic}
                    <button type="button" onClick={() => removeTopic(topic)} className="hover:text-red-400"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Platform Selection */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-brand-gray-300">Target Platforms</label>
          <p className="text-xs text-brand-gray-500">Posts will be generated separately for each platform with optimized content.</p>
          
          {/* Composio-enabled platforms */}
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-green-400 font-bold flex items-center gap-1.5">
              <Zap className="w-3 h-3" /> Direct Publish Available
            </p>
            <div className="flex flex-wrap gap-2">
              {['linkedin', 'facebook', 'instagram'].map(platformId => {
                const config = PLATFORM_CONFIG[platformId];
                const isSelected = profile.platforms.map(p => p.toLowerCase()).includes(platformId);
                const IconComponent = platformId === 'linkedin' ? Linkedin : platformId === 'facebook' ? Facebook : Instagram;
                
                return (
                  <button 
                    key={platformId} 
                    type="button" 
                    onClick={() => togglePlatform(config.name.split(' ')[0])} 
                    data-testid={`platform-${platformId}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      isSelected 
                        ? 'bg-green-900/30 text-green-400 ring-1 ring-green-500/50' 
                        : 'bg-black text-brand-gray-400 border border-brand-gray-700 hover:border-green-500/50'
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    {config.name.split(' /')[0]}
                    {isSelected && <Check className="w-3 h-3" />}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Manual export platforms */}
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-brand-gray-500 font-bold flex items-center gap-1.5">
              <Download className="w-3 h-3" /> Download & Copy
            </p>
            <div className="flex flex-wrap gap-2">
              {['twitter', 'tiktok', 'bluesky'].map(platformId => {
                const config = PLATFORM_CONFIG[platformId];
                const platformName = platformId === 'twitter' ? 'Twitter' : platformId === 'tiktok' ? 'TikTok' : 'BlueSky';
                const isSelected = profile.platforms.map(p => p.toLowerCase()).includes(platformId);
                const IconComponent = platformId === 'twitter' ? Twitter : platformId === 'tiktok' ? Video : Cloud;
                
                return (
                  <button 
                    key={platformId} 
                    type="button" 
                    onClick={() => togglePlatform(platformName)} 
                    data-testid={`platform-${platformId}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      isSelected 
                        ? 'bg-brand-orange/20 text-brand-orange ring-1 ring-brand-orange/50' 
                        : 'bg-black text-brand-gray-400 border border-brand-gray-700 hover:border-brand-gray-500'
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    {platformName}
                    {isSelected && <Check className="w-3 h-3" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Team Selector - Only show if user has teams */}
        {user && userTeams.length > 0 && (
          <div className="p-4 bg-emerald-900/10 border border-emerald-500/20 rounded-xl">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-medium text-emerald-300">
                <Users className="w-4 h-4" /> Save to Team
              </label>
              <select
                value={activeTeamId || ''}
                onChange={(e) => onTeamChange(e.target.value || null)}
                className="bg-black border border-brand-gray-700 rounded-lg px-3 py-1.5 text-sm text-white outline-none focus:ring-1 focus:ring-emerald-500"
              >
                <option value="">Personal (No Team)</option>
                {userTeams.map(t => (
                  <option key={t.team.id} value={t.team.id}>
                    {t.team.name} ({t.role})
                  </option>
                ))}
              </select>
            </div>
            {activeTeamId && (
              <p className="text-xs text-emerald-400/70 mt-2">
                This calendar will be shared with your team and visible in Team Content.
              </p>
            )}
          </div>
        )}

        {/* Submit Buttons */}
        <div className="flex flex-col-reverse md:flex-row gap-4 pt-4 border-t border-brand-gray-700/50">
          <div className="flex-1 md:flex-none flex flex-col gap-1">
            <button 
              type="button" 
              onClick={handleSaveProfile} 
              disabled={isSaving || (!profile.id && !canSaveNewProfile())}
              data-testid="save-profile-btn"
              className={`w-full px-6 py-3 rounded-xl font-medium border flex items-center justify-center gap-2 disabled:opacity-50 transition-all ${
                showSaveSuccess 
                  ? 'border-green-500 text-green-400' 
                  : !canSaveNewProfile() && !profile.id
                    ? 'border-amber-600 text-amber-400 bg-amber-900/10'
                    : 'text-white border-brand-gray-600 hover:bg-brand-gray-700'
              }`}
            >
              {isSaving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : showSaveSuccess ? (
                <Check className="w-5 h-5" />
              ) : !canSaveNewProfile() && !profile.id ? (
                <Lock className="w-5 h-5" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {isSaving ? 'Saving...' : showSaveSuccess ? 'Saved!' : (profile.id ? 'Update' : 'Save')}
              {user && !isSaving && !showSaveSuccess && <Cloud className="w-4 h-4 ml-1 opacity-50" />}
            </button>
            {/* Profile count indicator */}
            {user && (
              <div className={`text-[10px] text-center ${
                savedProfiles.length >= getMaxProfiles() 
                  ? 'text-amber-400' 
                  : 'text-brand-gray-500'
              }`}>
                {savedProfiles.length}/{getMaxProfiles() === Infinity ? '∞' : getMaxProfiles()} profiles saved
                {savedProfiles.length >= getMaxProfiles() && !profile.id && (
                  <span className="ml-1">(upgrade for more)</span>
                )}
              </div>
            )}
          </div>
          <button 
            type="submit" 
            disabled={isLoading} 
            data-testid="generate-strategy-btn"
            className={`flex-1 py-3 px-6 rounded-full font-headline uppercase tracking-wider text-lg shadow-xl transition-all ${isLoading ? 'bg-brand-gray-700 cursor-wait text-white' : 'bg-brand-orange text-black hover:bg-brand-orange/90'}`}
          >
            {isLoading ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> {profile.useTrendingNews ? 'Researching...' : 'Generating...'}</span> : 'Generate Strategy'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InputForm;
